// What an activity record (Outreach) proves. One rule for the follow-up queue
// (src/followupQueue.js), Mi espacio › Hoy and the tests.
//
// Several screens log an activity with Status "Sent" when Gmail, WhatsApp or
// LinkedIn is opened or a message is copied (Follow-up Center, Contactos, Quick
// Message, AI recommendation, Eventos, CP Briefings, Campañas, ContentLab,
// Landings). Opening a channel is not contacting someone: those records are
// `opened` and never count as a contact, never take anyone out of "Te
// respondieron" and never complete a priority. They stay in the history as they
// are (nothing is rewritten).
//
// Kinds:
//   inbound    — the contact wrote (Received, Replied, Meeting Booked…)
//   gmail_sent — an email Gmail recorded as sent ([gmsg:…] and Status Sent: sent
//                through Oike's Gmail or found in Sent by the Gmail sync)
//   confirmed  — logged after Ale confirmed she contacted the person (CONFIRMED_MARK)
//   logged     — any other outgoing activity registered by hand (a call, a meeting
//                scheduled, a quick log) or an older record of unknown origin
//   opened     — logged automatically when a channel was opened (see OPENED_NOTES)
//   ignored    — drafts and pending items: not an interaction
import { REPLY_STATUS_NAMES } from './replyStatuses.js';

export const CONFIRMED_MARK = '[oike-confirmado]';

/** The Notes those automatic "channel opened" logs start with (the code that writes them). */
export const OPENED_NOTES = [
  'Auto-logged from Follow-up Center', 'Sent from Contacts', 'Sent via Quick Message', '[AI Rec]',
  'Sent from Events Hub', 'Event invitation for', 'Post-event follow-up for', 'Auto-logged from CP Briefings',
  'Campaign: ', 'Content Lab share', 'Prospect Landing',
];

const val = (o, k) => { const v = o?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? v.value ?? '') : (v ?? ''); };

export function outreachKind(o) {
  const status = String(val(o, 'Status') || '');
  const notes = String(val(o, 'Notes') || '');
  if (REPLY_STATUS_NAMES.has(status)) return 'inbound';
  if (['Draft', 'Pending'].includes(status)) return 'ignored';
  if (notes.startsWith(CONFIRMED_MARK)) return 'confirmed';
  if (status === 'Sent' && /^\[gmsg:[^\]]+\]/.test(notes)) return 'gmail_sent';
  if (status === 'Sent' && OPENED_NOTES.some(p => notes.startsWith(p))) return 'opened';
  return 'logged';
}

/** An interaction that counts (theirs or a real contact of ours): everything except `opened` and `ignored`. */
export const countsAsInteraction = (o) => !['opened', 'ignored'].includes(outreachKind(o));
/** A contact of ours that counts. */
export const countsAsContact = (o) => ['gmail_sent', 'confirmed', 'logged'].includes(outreachKind(o));

/**
 * The focus tag and score of a contact (the same formula the commercial
 * enrichment uses: src/utils.jsx › computeEnrichment).
 */
export function focusFrom({ daysSince, influence = '', hasReplied = false, contactedToday = false }) {
  let focusScore = 0, focusTag = null;
  if (daysSince === null || daysSince === undefined) { focusScore += 30; focusTag = 'new'; }
  else if (daysSince >= 3 && daysSince <= 7) { focusScore += 55; focusTag = 'urgent'; }
  else if (daysSince > 7 && daysSince <= 21) { focusScore += 30; focusTag = 'followup'; }
  else if (daysSince > 21 && daysSince <= 45) { focusScore += 15; focusTag = 'reengage'; }
  else if (daysSince > 45) { focusScore += 5; focusTag = 'reengage'; }
  if (influence === 'Decision Maker') focusScore += 25;
  else if (influence === 'Champion') focusScore += 15;
  else if (influence === 'Influencer') focusScore += 10;
  if (hasReplied) focusScore += 20;
  if (contactedToday) focusScore = 0;
  return { focusScore, focusTag };
}

// ── What an activity sends to the AI (one rule for every screen, 04-11-2026) ──
// A record linked to a Gmail message ([gmsg:…] in Notes) keeps there the subject and a synced fragment (gmail-sync),
// and in Message the body sent from Oike (gmail-send). That text never goes to the AI just because it is stored: only
// when Ale explicitly chose that record (`allow`, a Set of record ids) for a function that needs it. Other activities
// keep their text, without Oike's internal marks. Date, channel, status and direction (metadata) always go. The
// records themselves never change.
const MARKS = /\[(oike-confirmado|oike:[^\]]+|op:[^\]]+|fecha:[^\]]+|gmsg:[^\]]+|gthread:[^\]]+|gmsgid:[^\]]+)\]\s*/g;
const NONE = new Set();
export const isGmailActivity = (o) => /\[gmsg:[^\]]+\]/.test(String(val(o, 'Notes') || ''));
/** The stored text of a Gmail activity (what Ale sees before choosing it), never sent unless chosen. */
export const gmailStoredText = (o) => (String(val(o, 'Message') || '').trim() || String(val(o, 'Notes') || '').replace(MARKS, '').trim());

export function aiTextOf(o, allow = NONE) {
  if (isGmailActivity(o)) return allow && allow.has && allow.has(o?.id) ? gmailStoredText(o) : '';
  return String(val(o, 'Message') || val(o, 'Notes') || '').replace(MARKS, '').trim();
}
/** Only the Message field (screens that never read Notes): none for a Gmail activity unless chosen. */
export const aiMessageOf = (o, allow = NONE) => (isGmailActivity(o) && !(allow && allow.has && allow.has(o?.id)) ? '' : String(val(o, 'Message') || '').trim());
/** The subject of an activity for the AI: none for a Gmail one unless chosen. */
export const aiSubjectOf = (o, allow = NONE) => (isGmailActivity(o) && !(allow && allow.has && allow.has(o?.id)) ? '' : String(val(o, 'Subject') || val(o, 'Topic') || ''));
/** The metadata that always goes: date, channel, status and who wrote. */
export const aiMetaOf = (o) => ({ date: String(val(o, 'Date') || ''), channel: String(val(o, 'Channel') || ''), status: String(val(o, 'Status') || ''), direction: outreachKind(o) === 'inbound' ? 'THEY' : 'YOU' });
/** The Gmail activities among `records` whose stored text a function would need: they are offered, unchecked, before generating. */
export const withheldGmailOf = (records = []) => { const seen = new Set(); return records.filter(o => o && isGmailActivity(o) && gmailStoredText(o) && !seen.has(o.id) && seen.add(o.id)); };
